/**
 * Task 214 — Corpus Reader heading presentation (pure logic).
 *
 * The cleaned transcripts use bracketed `### [...]` headings as the cleanup
 * contract (CLAUDE.md §Transcript Cleaning). Those files are immutable; this
 * module classifies the bracket contents at RENDER time so the reader can show
 * on-brand section markers instead of raw markup, and never show the
 * mechanical "[CONTINÚA EN EL SIGUIENTE SEGMENTO]" banner.
 *
 * Taxonomy (measured against the real corpus, 2026-06-10 — see spec 214):
 *   continuation  "[CONTINÚA EN EL SIGUIENTE SEGMENTO]" (415×, incl. unaccented)
 *   resume        "[CONTINÚA: Apocalipsis 1:8]", "[CONTINUIDAD DESDE LUCAS 9]",
 *                 "[Continuación: Salmo 2 ...]"
 *   verse         "[Apocalipsis 6:1-2]", "[GÉNESIS 1:14]"
 *   structural    "[INTRODUCCIÓN]", "[DESARROLLO]", "[CIERRE ...]",
 *                 "[CONEXIÓN ...]"; meta: "[NO-ENSEÑANZA]", "[Q&A ...]"
 *   topical       any other bracketed heading (the safe default — NEVER hide
 *                 teaching content on an unknown format)
 *
 * Chat answers are untouched: AnswerMarkdown stays as-is; only the corpus
 * renderer (CorpusMarkdown.tsx) consumes this module.
 */

export type HeadingKind = "continuation" | "resume" | "verse" | "structural" | "topical";

export interface CorpusHeading {
  kind: HeadingKind;
  /** Bracket contents, trimmed (original casing). */
  text: string;
  /** resume only: the referenced topic with the CONTINÚA/CONTINUIDAD prefix stripped. */
  resumeLabel?: string;
  /** structural only: meta sections (NO-ENSEÑANZA, Q&A) get a dimmed treatment. */
  meta?: boolean;
}

export type CorpusBlock =
  | { type: "md"; md: string }
  | { type: "heading"; heading: CorpusHeading };

/** Uppercase + strip combining accents, for tolerant marker matching. */
function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();
}

const VERSE_RE = /^[\p{L}0-9][\p{L}0-9\s.]*?\s\d+:\d+(?:-\d+)?$/u;
const STRUCTURAL_PREFIXES = [
  "INTRODUCCION",
  "DESARROLLO",
  "CIERRE",
  "CONEXION",
  "ORACION FINAL",
];
const META_PREFIXES = ["NO-ENSENANZA", "NO ENSENANZA", "Q&A", "QA FINAL"];

export function classifyHeading(inner: string): CorpusHeading {
  const text = inner.trim();
  const folded = fold(text);

  // Mechanical chunk-boundary marker — never shown as text.
  if (/^CONTINUA EN EL SIGUIENTE SEGMENTO$/.test(folded)) {
    return { kind: "continuation", text };
  }

  // Resume-of-topic headings.
  if (/^(CONTINUA|CONTINUIDAD|CONTINUACION)\b/.test(folded)) {
    const afterColon = /^[^:]*:\s*(.+)$/.exec(text)?.[1];
    const afterPrep = /^continuidad\s+(?:desde|de la|del|de|con)\s+(.+)$/i.exec(text)?.[1];
    const label = (afterColon ?? afterPrep ?? text).trim();
    return { kind: "resume", text, resumeLabel: label };
  }

  // Verse references: "<book words> <chapter>:<verse>[-<verse>]".
  if (VERSE_RE.test(text)) {
    return { kind: "verse", text };
  }

  for (const p of META_PREFIXES) {
    if (folded.startsWith(p)) return { kind: "structural", text, meta: true };
  }
  for (const p of STRUCTURAL_PREFIXES) {
    if (folded.startsWith(p)) return { kind: "structural", text, meta: false };
  }

  // Safe default: strip brackets, render as a topical heading.
  return { kind: "topical", text };
}

const HEADING_LINE_RE = /^###\s+\[([^\]]+)\]\s*$/;

/**
 * Split cleaned-chunk markdown into plain-md runs and classified bracket
 * headings. Non-bracketed headings and everything else pass through as md
 * (rendered by the normal markdown pipeline, byte-identical).
 */
export function parseCorpusBlocks(md: string): CorpusBlock[] {
  const blocks: CorpusBlock[] = [];
  let run: string[] = [];
  const flush = (): void => {
    const joined = run.join("\n");
    if (joined.trim().length > 0) blocks.push({ type: "md", md: joined });
    run = [];
  };
  for (const line of md.split("\n")) {
    const m = HEADING_LINE_RE.exec(line);
    if (m && m[1] !== undefined) {
      flush();
      blocks.push({ type: "heading", heading: classifyHeading(m[1]) });
    } else {
      run.push(line);
    }
  }
  flush();
  return blocks;
}
